import { FC } from 'react';
import { useProjectContext } from '@/providers/ProjectContextProvider';
import { CheckboxTree } from '@/components/generic/treeSelector/CheckboxTree';

type EnterpriseTreeSelectorProps = {
  title?: string;
  selectedOrgUnit: { id: string; name: string }[];
  setSelectedOrgUnit: (selected: { id: string; name: string }[]) => void;
};

type OrganizationalUnitNode = {
  children: OrganizationalUnitNode[];
  id: string;
  name: string;
};
export const EnterpriseTreeSelector: FC<EnterpriseTreeSelectorProps> = ({
  title,
  selectedOrgUnit = [],
  setSelectedOrgUnit,
}) => {
  const projectContext = useProjectContext();
  const flattenOrganizationalUnits =
    projectContext?.flattenOrganizationalUnits || [];

  const getParentNode = (
    parentId: string,
  ): OrganizationalUnitNode | undefined => {
    const parent = flattenOrganizationalUnits.find((ou) => ou.id === parentId);

    if (!parent) {
      return;
    }

    const parentNode = {
      id: parent.id,
      name: parent.name,
      children: [],
    };

    if (!parent.parent) {
      return parentNode;
    }

    const grandparentNode = getParentNode(parent.parent.id);
    if (!grandparentNode) {
      return parentNode;
    }

    grandparentNode.children.push(parentNode);

    return grandparentNode;
  };

  const findNodeInTree = (
    id: string,
    children: OrganizationalUnitNode[],
  ): OrganizationalUnitNode | undefined => {
    const child = children.find((child) => child.id === id);

    if (child) {
      return child;
    }

    for (const child of children) {
      const found = findNodeInTree(id, child.children);
      if (found) return found;
    }
  };

  const getOrganizationalUnitsTree = () => {
    const organizationalUnitsTree: OrganizationalUnitNode[] = [];

    for (const ou of flattenOrganizationalUnits) {
      const existing = findNodeInTree(ou.id, organizationalUnitsTree);
      if (existing) continue;

      const currentNode = {
        id: ou.id,
        name: ou.name,
        children: [],
      };

      const parentId = ou.parent?.id;

      if (!parentId) {
        organizationalUnitsTree.push(currentNode);

        continue;
      }

      const parentInTree = findNodeInTree(parentId, organizationalUnitsTree);
      if (parentInTree) {
        parentInTree.children.push(currentNode);
        continue;
      }

      const parentNode = getParentNode(parentId);
      if (parentNode) {
        parentNode.children.push(currentNode);
        organizationalUnitsTree.push(parentNode);
      }
    }

    return organizationalUnitsTree;
  };

  return (
    <CheckboxTree
      selectedValues={selectedOrgUnit}
      title={title}
      onChange={setSelectedOrgUnit}
      treeData={getOrganizationalUnitsTree()}
      valueFieldName={'name'}
      childrenFieldName={'children'}
    />
  );
};
